import type React from "react";

import {DEFAULT_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH, useExtensionStore} from "@project/app-state";
import {
  Button,
  CloseButton,
  Resizable,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";

import {appShellMessages} from "../../messages/app-shell.messages";

import {ContentPortalBoundary} from "./content-portal-boundary";
import {
  sidebarExtensionList,
  sidebarExtensionsById,
} from "./right-sidebar/extension-sidebar-registry";

interface WorkspaceSplitProps {
  children: React.ReactNode;
}

function ActiveExtensionPanel() {
  const {t} = useI18n();
  const activeExtension = useExtensionStore((s) => s.activeExtension);
  const toggleSidebar = useExtensionStore((s) => s.toggleSidebar);
  const activeSidebarExtension = sidebarExtensionsById[activeExtension];
  const Panel = activeSidebarExtension?.panel;
  const title = activeSidebarExtension
    ? t(activeSidebarExtension.labelDescriptor)
    : t(appShellMessages.extensions);

  return (
    <div className="flex h-full min-h-0 flex-col" data-react-aria-top-layer="true">
      <div className="border-border flex h-12 shrink-0 items-center justify-between border-b px-3">
        <Typography className="truncate text-sm font-semibold">{title}</Typography>
        <CloseButton onPress={() => toggleSidebar()} />
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden">{Panel ? <Panel /> : null}</div>
    </div>
  );
}

function ExtensionRail({className = ""}: {className?: string}) {
  const {t} = useI18n();
  const {isOpen, activeExtension} = useExtensionStore((s) => ({
    isOpen: s.isOpen,
    activeExtension: s.activeExtension,
  }));
  const toggleSidebar = useExtensionStore((s) => s.toggleSidebar);
  const setActiveExtension = useExtensionStore((s) => s.setActiveExtension);

  return (
    <div
      className={`border-border bg-background flex w-12 shrink-0 flex-col items-center gap-2 border-l py-2 ${className}`}
      data-react-aria-top-layer="true"
    >
      {sidebarExtensionList.map((extension) => {
        const label = t(extension.labelDescriptor);

        return (
          <Tooltip key={extension.id} delay={300}>
            <Button
              isIconOnly
              aria-label={label}
              size="sm"
              variant={isOpen && activeExtension === extension.id ? "secondary" : "ghost"}
              onPress={() => {
                if (isOpen && activeExtension === extension.id) {
                  toggleSidebar();
                } else {
                  setActiveExtension(extension.id);
                }
              }}
            >
              {extension.icon}
            </Button>
            <Tooltip.Content placement="left">
              <p>{label}</p>
            </Tooltip.Content>
          </Tooltip>
        );
      })}
    </div>
  );
}

export function WorkspaceSplit({children}: WorkspaceSplitProps) {
  const {isOpen, width} = useExtensionStore((s) => ({isOpen: s.isOpen, width: s.width}));
  const setWidth = useExtensionStore((s) => s.setWidth);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const viewportWidth = typeof window === "undefined" ? 1440 : Math.max(window.innerWidth, 1);

  const defaultExtensionSize = Math.min(50, Math.round((width / viewportWidth) * 100));
  const minExtensionSize = Math.round((MIN_SIDEBAR_WIDTH / viewportWidth) * 100);
  const fallbackExtensionSize = Math.min(
    50,
    Math.round((DEFAULT_SIDEBAR_WIDTH / viewportWidth) * 100),
  );

  if (!isDesktop) {
    return (
      <div className="flex h-full min-h-0 overflow-hidden">
        <div className="min-w-0 flex-1">
          <ContentPortalBoundary>
            <div className="relative h-full min-h-0">
              <div
                aria-hidden={isOpen}
                className={isOpen ? "hidden h-full min-h-0" : "h-full min-h-0"}
              >
                {children}
              </div>

              <div
                aria-hidden={!isOpen}
                className={
                  isOpen
                    ? "absolute inset-0 z-30 flex min-h-0 flex-col"
                    : "absolute inset-0 hidden min-h-0 flex-col"
                }
              >
                <ActiveExtensionPanel />
              </div>

              {isOpen && sidebarExtensionList.length > 1 ? (
                <ExtensionRail className="shadow-overlay absolute top-1/2 right-2 z-40 max-h-[60dvh] -translate-y-1/2 rounded-2xl border" />
              ) : null}
            </div>
          </ContentPortalBoundary>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 overflow-hidden">
      <div className="min-w-0 flex-1">
        {isOpen ? (
          <Resizable autoSaveId="app-shell:right-extension" className="h-full w-full">
            <Resizable.Panel defaultSize={100 - (defaultExtensionSize || fallbackExtensionSize)}>
              <ContentPortalBoundary>{children}</ContentPortalBoundary>
            </Resizable.Panel>
            <Resizable.Handle type="line" withIndicator />
            <Resizable.Panel
              defaultSize={defaultExtensionSize || fallbackExtensionSize}
              id="right-extension"
              maxSize={50}
              minSize={minExtensionSize || 18}
              onResize={(size) => {
                const currentViewportWidth =
                  typeof window === "undefined" ? viewportWidth : window.innerWidth;

                setWidth(Math.round((currentViewportWidth * Math.min(50, Number(size))) / 100));
              }}
            >
              <ActiveExtensionPanel />
            </Resizable.Panel>
          </Resizable>
        ) : (
          <ContentPortalBoundary>{children}</ContentPortalBoundary>
        )}
      </div>

      <ExtensionRail className="hidden lg:flex" />
    </div>
  );
}
